import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { ColumnAlign, ColumnType, GridColumnInfo, GridConfig, GridRow } from '@inavista/components/grid';
import { GridElement } from 'projects/service/src/app/modules/builder/models/grid.model';
import { LocalRowsGridApi } from '../../utils/local-rows-grid-api';

// The grid of a form builder "grid" element (app-syncfusion-grid, rendered by app-dynamic-popup-layout) on
// @inavista/components. The columns, labels, order, visibility, widths, alignment, sort/filter switches, toolbar and
// status / link columns come from the element's definition (trn.pages.elements), read the way the Syncfusion
// wrapper read them (setGridColumns / setGridToolbar). Rows are the form's own field (element.dataSource), served by
// LocalRowsGridApi. No paging (Martin): the grid scrolls.
// On Rims140Dev one popup form has a grid: PG0000149 Adjustment Order Popup, Exceptions tab
// (electronicAdjustmentOrderLines). No calibration form has one.

export const DYNAMIC_FORM_GRID_URL = 'page://dynamic-form-grid';
/** Fallback key, for a caller that gives no persistenceKey (none today). */
export const SYNCFUSION_GRID_WRAPPER_PERSISTENCE_KEY = 'rims.service.syncfusion-grid-wrapper';
/** One key per form grid: the element's key is its identity in the form definition. */
export const dynamicFormGridPersistenceKey = (elementKey: string) => `rims.service.dynamic-form-grid.${elementKey}`;

/** A row number the page adds to each row, so the grid has a key whatever the definition's columns are. */
export const ROW_KEY = '__rowNo';

export interface DynamicFormGridColumn {
  field: string;
  headerText?: string;
  visible?: boolean;
  showInColumnChooser?: boolean;
  width?: string | number;
  type?: string;
  textAlign?: string;
  headerTextAlign?: string;
  allowSorting?: boolean;
  filter?: { type?: string };
  columnType?: string;
  commands?: unknown[];
}

export interface DynamicFormGridContext {
  /** Manager, CSA or administrator: the old wrapper showed technicianName to these roles only. */
  canSeeTechnician: boolean;
  canExport: boolean;
  statusColor: (value: unknown) => string;
  persistenceKey: string;
}

// The old wrapper dropped the Actions (command) column for non-managers, and Syncfusion command buttons have no
// definition in any form on Rims140Dev; a command column is not a data column, so it is not shown.
const isDataColumn = (c: DynamicFormGridColumn) => !!c?.field && c.field !== 'Actions' && !(c.commands && c.commands.length);

const SQL_TYPES: Record<string, string> = { number: 'decimal', boolean: 'bit', date: 'datetime', datetime: 'datetime' };
const COLUMN_TYPES: Record<string, ColumnType> = { number: 'number', boolean: 'boolean', date: 'date', datetime: 'datetime' };

const align = (a?: string): ColumnAlign['horizontal'] =>
  a === 'Right' ? 'right' : a === 'Center' ? 'center' : 'left';

export function dynamicFormGridColumns(grid: GridElement): DynamicFormGridColumn[] {
  return ((grid?.columns ?? []) as DynamicFormGridColumn[]).filter(isDataColumn);
}

export function dynamicFormGridSchema(grid: GridElement): GridColumnInfo[] {
  return LocalRowsGridApi.schema([
    [ROW_KEY, 'int'],
    ...dynamicFormGridColumns(grid).map(c => [c.field, SQL_TYPES[c.type] ?? 'nvarchar'] as [string, string]),
  ]);
}

/**
 * The rows as the old grid showed them: its Query kept `isDeleted != true`, except on the 'Adjustment Orders' grid,
 * whose Query only added a company parameter (meaningless for rows the page holds).
 */
export function dynamicFormGridRows(grid: GridElement, rows: unknown): GridRow[] {
  const list = Array.isArray(rows) ? rows : [];
  const kept = grid?.elementLabel === 'Adjustment Orders' ? list : list.filter(r => r?.isDeleted !== true);
  return kept.map((r, i) => ({ ...r, [ROW_KEY]: i + 1 }));
}

export function buildDynamicFormGridConfig(grid: GridElement, ctx: DynamicFormGridContext): GridConfig {
  const columns = dynamicFormGridColumns(grid);
  const fields = columns.map(c => c.field);
  const technicianHidden = (c: DynamicFormGridColumn) => c.field === 'technicianName' && !ctx.canSeeTechnician;
  // Syncfusion: visible:false + showInColumnChooser:false never showed and could not be picked; visible:false
  // alone started hidden but could be picked; showInColumnChooser:false alone could not be hidden.
  const notPickable = columns.filter(c => technicianHidden(c) || (c.visible === false && c.showInColumnChooser === false));
  const startHidden = columns.filter(c => !notPickable.includes(c) && c.visible === false);
  const alwaysVisible = columns.filter(c => c.visible !== false && c.showInColumnChooser === false && !technicianHidden(c));
  const showToolbar = !!grid.showToolbar;
  return {
    apiBaseUrl: DYNAMIC_FORM_GRID_URL,
    clientSide: true,
    keyColumn: ROW_KEY,
    rowHeight: 30,
    defaultColumnOrder: fields,
    hiddenByDefaultColumns: [ROW_KEY, ...notPickable.map(c => c.field)],
    startHiddenColumns: startHidden.map(c => c.field),
    alwaysVisibleColumns: alwaysVisible.map(c => c.field),
    columnLabels: Object.fromEntries(columns.map(c => [c.field, c.headerText ?? c.field])),
    columnAlign: Object.fromEntries(columns.map(c => [c.field, {
      horizontal: align(c.textAlign), header: { horizontal: align(c.headerTextAlign ?? c.textAlign) },
    }])),
    // The definition's widths; Syncfusion spread the spare width over every column, as stretchColumns does.
    columnWidths: Object.fromEntries(columns.filter(c => Number(c.width) > 0).map(c => [c.field, Number(c.width)])),
    stretchColumns: true,
    columnTypes: Object.fromEntries(columns.filter(c => COLUMN_TYPES[c.type]).map(c => [c.field, COLUMN_TYPES[c.type]])),
    // The old wrapper formatted 'date' columns as 'd MMM, y'; other types kept Syncfusion's raw value.
    columnDateFormats: Object.fromEntries(columns.filter(c => c.type === 'date').map(c => [c.field, 'd MMM, yyyy'])),
    columnNumberFormats: Object.fromEntries(columns.filter(c => c.type === 'number')
      .map(c => [c.field, { useGrouping: false, maximumFractionDigits: 20 }])),
    // A 'ClickableTemplate' column was a link; any other columnType was the status pill.
    hyperlinkColumns: columns.filter(c => c.columnType?.includes('ClickableTemplate')).map(c => c.field),
    columnColors: Object.fromEntries(columns.filter(c => c.columnType && !c.columnType.includes('ClickableTemplate'))
      .map(c => [c.field, ctx.statusColor])),
    unsortableColumns: grid.allowSorting ? columns.filter(c => c.allowSorting === false).map(c => c.field) : fields,
    excelFilterColumns: columns.filter(c => c.filter?.type === 'Excel').map(c => c.field),
    multiSort: !!grid.allowSorting,
    selectable: false,
    // The old toolbar (showToolbar only): Columns, Search, Excel export (allowExcelExport + AllGrid_Toolbar_Excel_Export), Clear filters.
    enableToolbar: showToolbar,
    enableColumnsPicker: showToolbar,
    enableSearch: showToolbar,
    enableFiltering: !!grid.allowFiltering,
    enableExport: showToolbar && !!grid.allowExcelExport && ctx.canExport,
    enableSavedViews: false,
    enableViewsPicker: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    loadingIndicator: 'banner',
    emptyStateMessage: 'No records to display',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: ctx.persistenceKey,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
